import { Link, usePage } from '@inertiajs/react'
import { ReactNode } from 'react'

import { Wordmark } from './Wordmark'
import { cn } from '@/lib/utils'
import { Toaster } from '@/components/ui/sonner'

interface AdminLayoutProps {
  children: ReactNode
}

const NAV = [
  { label: 'Dashboard', href: '/admin/dashboard' },
  { label: 'Products', href: '/admin/products' },
  { label: 'Orders', href: '/admin/orders' },
  { label: 'Waitlist', href: '/admin/stock_requests' },
  { label: 'Funnel', href: '/admin/funnel' },
  { label: 'Visitors', href: '/admin/visitors' },
]

/**
 * Spec 11 · Admin shell.
 *
 * "Same tokens, denser: 12px body, 36px controls, tables on surface
 * with hairline rows. Accent only for the active filter and primary
 * action." The bar is ink with a 20px wordmark; the active nav item is
 * the one accent in the chrome.
 */
export default function AdminLayout({ children }: AdminLayoutProps) {
  const { url } = usePage()
  const path = url.split('?')[0]

  const isActive = (href: string) =>
    href === '/admin/dashboard'
      ? path === '/admin' || path.startsWith('/admin/dashboard')
      : path.startsWith(href)

  return (
    <div className="sss-admin min-h-dvh bg-background">
      <header className="sss-ink-field flex flex-wrap items-center justify-between gap-4 px-[18px] py-3">
        <div className="flex flex-wrap items-center gap-[22px]">
          <Link href="/admin" aria-label="Sick Stuff admin">
            <Wordmark size={20} className="text-ink" />
          </Link>
          <nav className="flex flex-wrap items-center gap-[22px]" aria-label="Admin">
            {NAV.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                aria-current={isActive(item.href) ? 'page' : undefined}
                className={cn(
                  'text-[13px] font-medium leading-none transition-colors duration-[120ms]',
                  isActive(item.href)
                    ? 'text-signal'
                    : 'text-ink-muted hover:text-ink'
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>

        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          className="text-[13px] font-medium leading-none text-ink-muted transition-colors duration-[120ms] hover:text-ink"
        >
          View site ↗
        </a>
      </header>

      <main className="mx-auto max-w-[1440px] p-[18px]">{children}</main>
      <Toaster />
    </div>
  )
}
